import { useEffect, useRef, useState } from 'react'

import type { StepView } from '../../lib/api'
import { turnstileSiteKey } from '../../lib/tenant'

/*
 * Cloudflare Turnstile, on the steps the FLOW marks with `requiresChallenge`.
 *
 * Where a challenge appears is a routing decision and it lives in
 * canonical.ts with every other one — always on the step immediately BEFORE a
 * write, never on the write itself, because an action step has no UI to render
 * a widget into and a token collected any earlier would have expired.
 *
 * With no site key configured this is inert and submission is unblocked, which
 * matches the server: apps/web/src/lib/turnstile.ts fails open on missing
 * configuration, so a half-configured deploy cannot stop real customers
 * booking.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
      reset: (id?: string) => void
    }
  }
}

const SCRIPT_ID = 'cf-turnstile-script'
const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
  if (existing) {
    return new Promise((resolve) =>
      existing.addEventListener('load', () => resolve(), { once: true }),
    )
  }

  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve(), { once: true })
    document.head.appendChild(script)
  })
}

/**
 * `onToken` receives the solved token, or null when it expires.
 *
 * `resetKey` forces a fresh challenge: a token is single-use, so after a
 * rejected submission the one we hold is spent and re-submitting the same
 * string would fail forever.
 */
function Turnstile({
  siteKey,
  onToken,
  resetKey,
}: {
  siteKey: string
  onToken: (token: string | null) => void
  resetKey?: string
}) {
  const host = useRef<HTMLDivElement>(null)
  // Held in a ref so the effect does not re-run when the callback identity
  // changes, which would rebuild the widget on every parent state update.
  const cb = useRef(onToken)
  cb.current = onToken

  useEffect(() => {
    let widgetId: string | undefined
    let cancelled = false

    void loadScript().then(() => {
      if (cancelled || !host.current || !window.turnstile) return
      host.current.innerHTML = ''
      widgetId = window.turnstile.render(host.current, {
        sitekey: siteKey,
        callback: (token: string) => cb.current(token),
        'expired-callback': () => cb.current(null),
        'error-callback': () => cb.current(null),
        // The app is light-only for now; `auto` would draw a dark widget on a
        // light page for anyone with a dark system setting.
        theme: 'light',
      })
    })

    return () => {
      cancelled = true
      // Removed rather than left parked: React may reuse this node for a
      // different step, and a stale widget would hand back a spent token.
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId)
        } catch {
          // Already gone; nothing to undo.
        }
      }
    }
  }, [siteKey, resetKey])

  return <div ref={host} style={{ marginTop: 4 }} />
}

export interface Challenge {
  /** False when submission must wait for a token. */
  ready: boolean
  /** Merged into the step payload. Empty when no challenge is asked for. */
  payload: Record<string, unknown>
  node: React.ReactNode
}

export function useChallenge(view: StepView): Challenge {
  const { step } = view
  const required =
    (step.type === 'review' || step.type === 'form') &&
    step.requiresChallenge === true &&
    turnstileSiteKey !== ''

  const [token, setToken] = useState<string | null>(null)

  /*
   * A token is single-use. After a rejected submission the one we hold is
   * spent, so the widget has to be rebuilt rather than re-read — otherwise the
   * customer retries forever with a string the server has already burned.
   */
  const attempt = `${step.id}:${view.errors?.__challenge ?? ''}`
  const [lastAttempt, setLastAttempt] = useState(attempt)
  if (attempt !== lastAttempt) {
    setLastAttempt(attempt)
    setToken(null)
  }

  return {
    // Never block on a challenge that is not configured or not asked for.
    ready: !required || token !== null,
    payload: token ? { turnstileToken: token } : {},
    node: required ? (
      <div>
        {view.errors?.__challenge ? (
          <p className="blooq-err" style={{ marginBottom: 6 }}>
            {view.errors.__challenge}
          </p>
        ) : null}
        <Turnstile siteKey={turnstileSiteKey} onToken={setToken} resetKey={attempt} />
      </div>
    ) : null,
  }
}
